import { c as _c } from "react/compiler-runtime";
import { IcoBuilding16, IcoHome16 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useAddressFieldsProps from '../../hooks/use-address-fields-props';
import useScrollIntoView from '../../hooks/use-scroll-into-view';
import AddressType from './types';
const CONTENT_CLASS_NAME = 'grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 py-2 pl-3 text-left';
type AddressRowProps = {
  id: string;
  type: AddressType;
  entity: Entity;
  isPending: boolean;
  isSelected: boolean;
  /** Changes with each pick of the row's pin, which scrolls the list to it. */
  mapPick?: number;
  /** Has a pin on the map; until it's decrypted and found, there's nothing to select. */
  isLocatable: boolean;
  onSelect: (id: string) => void;
};
const AddressRow = t0 => {
  const $ = _c(48);
  const {
    id,
    type,
    entity,
    isPending,
    isSelected,
    mapPick,
    isLocatable,
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights.addressCard"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== entity.data || $[2] !== entity.id) {
    t2 = {
      entityAttributes: entity.data,
      entityId: entity.id
    };
    $[1] = entity.data;
    $[2] = entity.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const entityVaultQuery = useEntityVault(t2);
  const {
    getAddressFieldsProps
  } = useAddressFieldsProps(entity);
  const decryptControls = useDecryptControls();
  const ref = useScrollIntoView(mapPick);
  let Icon;
  let address;
  let t3;
  if ($[4] !== decryptControls || $[5] !== entity || $[6] !== entityVaultQuery || $[7] !== getAddressFieldsProps || $[8] !== isPending || $[9] !== t || $[10] !== type) {
    let t4;
    if ($[14] !== entity.data) {
      t4 = field => entity.data.some(t5 => {
        const {
          identifier
        } = t5;
        return identifier === field.name;
      });
      $[14] = entity.data;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    const fields = getAddressFieldsProps(type).filter(t4);
    const encryptedFields = fields.filter(_temp);
    const encryptedDis = encryptedFields.map(_temp2);
    const canDecrypt = encryptedFields.some(_temp3);
    const isLoading = isPending || decryptControls.isDecrypting(encryptedDis);
    address = encryptedFields.length ? undefined : fields.map(_temp4).filter(_temp5).join(", ");
    Icon = type === AddressType.residential ? IcoHome16 : IcoBuilding16;
    const handleDecrypt = async () => {
      const response = await decryptControls.decrypt({
        entity,
        fields: encryptedDis,
        reason: t(_temp6)
      });
      if (response) {
        entityVaultQuery.update({
          vault: response,
          transforms: {},
          dataKinds: {}
        });
      }
    };
    const renderAction = () => {
      if (isLoading) {
        return <LoadingSpinner size="compact" />;
      }
      return canDecrypt && <LinkButton variant="label-3" onClick={handleDecrypt}>{t(_temp7)}</LinkButton>;
    };
    t3 = renderAction();
    $[4] = decryptControls;
    $[5] = entity;
    $[6] = entityVaultQuery;
    $[7] = getAddressFieldsProps;
    $[8] = isPending;
    $[9] = t;
    $[10] = type;
    $[11] = Icon;
    $[12] = address;
    $[13] = t3;
  } else {
    Icon = $[11];
    address = $[12];
    t3 = $[13];
  }
  const action = t3;
  const secondaryText = isLocatable && isSelected ? "text-secondary" : "text-tertiary";
  let t4;
  if ($[16] !== Icon) {
    t4 = <span aria-hidden={true} className="mt-0.5 flex"><Icon color="tertiary" /></span>;
    $[16] = Icon;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t || $[19] !== type) {
    t5 = type === AddressType.residential ? t(_temp8) : t(_temp9);
    $[18] = t;
    $[19] = type;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t5) {
    t6 = <span className="truncate text-label-2 text-primary">{t5}</span>;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== address || $[24] !== secondaryText || $[25] !== t) {
    t7 = address ? <span className={cx("truncate text-body-3", secondaryText)} data-dd-privacy="mask">{address}</span> : <span className={cx("text-body-3", secondaryText)}>{t(_temp10)}</span>;
    $[23] = address;
    $[24] = secondaryText;
    $[25] = t;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t6 || $[28] !== t7) {
    t8 = <span className="flex min-w-0 flex-col gap-0.5">{t6}{t7}</span>;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t4 || $[31] !== t8) {
    t9 = <>{t4}{t8}</>;
    $[30] = t4;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const content = t9;
  const t10 = isLocatable && (isSelected ? "bg-hover-control" : "hover:bg-hover");
  let t11;
  if ($[33] !== t10) {
    t11 = cx("relative flex scroll-my-6 items-center gap-3 rounded-default pr-3 transition-colors duration-100", t10);
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== content || $[36] !== id || $[37] !== isLocatable || $[38] !== isSelected || $[39] !== onSelect) {
    t12 = isLocatable ? <button type="button" aria-current={isSelected || undefined} onClick={() => onSelect(id)} className={cx(CONTENT_CLASS_NAME, "cursor-pointer after:absolute after:inset-0 after:rounded-default")}>{content}</button> : <div className={CONTENT_CLASS_NAME}>{content}</div>;
    $[35] = content;
    $[36] = id;
    $[37] = isLocatable;
    $[38] = isSelected;
    $[39] = onSelect;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== action) {
    t13 = action && <span className="relative z-10 flex">{action}</span>;
    $[41] = action;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== ref || $[44] !== t11 || $[45] !== t12 || $[46] !== t13) {
    t14 = <li ref={ref} className={t11}>{t12}{t13}</li>;
    $[43] = ref;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
export default AddressRow;
function _temp(field_0) {
  return !field_0.isDecrypted;
}
function _temp2(field_1) {
  return field_1.name;
}
function _temp3(field_2) {
  return field_2.canDecrypt;
}
function _temp4(t0) {
  const {
    value
  } = t0;
  return value;
}
function _temp5(value_0) {
  return typeof value_0 === "string" && value_0.length > 0;
}
function _temp6($) {
  return $.decryptReason;
}
function _temp7($_0) {
  return $_0.decrypt;
}
function _temp8($_1) {
  return $_1.residential.headerText;
}
function _temp9($_2) {
  return $_2.business.headerText;
}
function _temp10($_3) {
  return $_3.encrypted;
}